// RegimeTab.jsx — v9.1 MTF Regime State Visualisierung
//
// Zeigt für einen Optimizer-Trial die Daily- und H1-Regime-Phasen
// als Zeit-Streifen, mit Trade-Markern darüber.
//
// Daten-Quellen (aus run-detail JSON):
//   data.mtf_regime_data_per_trial[trial_id] = {
//     schema_version, n_daily_bars, n_h1_bars,
//     daily_transitions: [{bar_idx, ts, phase, trend}, ...],
//     h1_transitions:    [{bar_idx, ts, phase, trend}, ...],
//   }
//   data.trades_per_trial[trial_id] = [{entry_time, exit_time, pnl, direction,
//                                       mtf_daily_phase, mtf_daily_trend,
//                                       mtf_h1_phase, mtf_h1_trend, ...}, ...]
//
// Phase-Codes:  0=UNDEF (vor erster Struktur), 1=ETAB, 2=PIVOT (Übergang)
// Trend-Codes: -1=BEAR, 0=neutral, 1=BULL

import React, { useMemo, useState, useEffect } from "react";
import { theme, fonts } from "../../theme";
import RegimeChart from "./RegimeChart";
import H1DetailChart from "./H1DetailChart";

// ─── Helpers ────────────────────────────────────────────────

function regimeLabel(phase, trend) {
  if (phase === 0) return "Undefined";
  if (phase === 2) return trend === 1 ? "Pivot Bull" : trend === -1 ? "Pivot Bear" : "Pivot";
  if (phase === 1) return trend === 1 ? "ETAB Bull" : trend === -1 ? "ETAB Bear" : "ETAB neutral";
  return "?";
}

function regimeColor(phase, trend) {
  if (phase === 0) return "#cbd5e1";           // grau für Undefined
  if (phase === 2) return "#f97316";           // orange für Pivot (egal welcher trend)
  if (phase === 1) {
    if (trend === 1)  return "#16a34a";        // grün für ETAB Bull
    if (trend === -1) return "#dc2626";        // rot für ETAB Bear
    return "#94a3b8";                          // neutral grau
  }
  return "#94a3b8";
}

function parseDate(s) {
  if (!s) return null;
  // ISO mit oder ohne Timezone
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function formatDateShort(d) {
  if (!d) return "—";
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function formatPnl(v) {
  if (v == null) return "—";
  const sign = v >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(v).toFixed(0)}`;
}

/**
 * Bauen aus transitions[] die Segments-Liste:
 *   [{start: Date, end: Date, phase, trend, color, label}, ...]
 *
 * Letztes Segment läuft bis runEnd.
 */
function buildSegments(transitions, runEnd) {
  if (!Array.isArray(transitions) || transitions.length === 0) return [];
  const segs = [];
  for (let i = 0; i < transitions.length; i++) {
    const t = transitions[i];
    const startDt = parseDate(t.ts);
    if (!startDt) continue;
    const endDt = i+1 < transitions.length ? parseDate(transitions[i+1].ts) : runEnd;
    if (!endDt) continue;
    segs.push({
      start: startDt,
      end: endDt,
      phase: t.phase,
      trend: t.trend,
      color: regimeColor(t.phase, t.trend),
      label: regimeLabel(t.phase, t.trend),
    });
  }
  return segs;
}

// ─── Sub-Components ─────────────────────────────────────────

/** Render ein Zeit-Streifen (Daily oder H1) auf dem SVG-Canvas */
function RegimeStrip({ segments, x0, y, width, height, runStart, runEnd, onHover }) {
  const totalMs = runEnd - runStart;
  if (totalMs <= 0) return null;
  return (
    <g>
      {segments.map((seg, i) => {
        const xStart = x0 + ((seg.start - runStart) / totalMs) * width;
        const xEnd   = x0 + ((seg.end   - runStart) / totalMs) * width;
        const segW = Math.max(1, xEnd - xStart);
        return (
          <rect
            key={i}
            x={xStart} y={y} width={segW} height={height}
            fill={seg.color}
            opacity={seg.phase === 0 ? 0.25 : 0.85}
            onMouseEnter={() => onHover && onHover({type: "segment", seg})}
            onMouseLeave={() => onHover && onHover(null)}
            style={{cursor: "default"}}
          />
        );
      })}
    </g>
  );
}

/** Trade-Marker auf dem SVG-Canvas */
function TradeMarkers({ trades, x0, y, width, runStart, runEnd, maxAbsPnl, onHover }) {
  const totalMs = runEnd - runStart;
  if (totalMs <= 0 || !trades || !trades.length) return null;
  return (
    <g>
      {trades.map((t, i) => {
        const entryDt = parseDate(t.entry_time);
        if (!entryDt) return null;
        const x = x0 + ((entryDt - runStart) / totalMs) * width;
        const pnl = t.pnl || 0;
        const isWin = pnl > 0;
        // Radius: skaliere mit |pnl|, aber clamp auf vernünftige Range
        const r = Math.max(2, Math.min(6, 2 + (Math.abs(pnl) / Math.max(maxAbsPnl, 1)) * 4));
        return (
          <circle
            key={i}
            cx={x} cy={y} r={r}
            fill={isWin ? "#16a34a" : "#dc2626"}
            stroke="white" strokeWidth={0.5}
            opacity={0.85}
            onMouseEnter={() => onHover && onHover({type: "trade", trade: t})}
            onMouseLeave={() => onHover && onHover(null)}
            style={{cursor: "pointer"}}
          />
        );
      })}
    </g>
  );
}

/** Monats-Achse unter der Timeline */
function TimeAxis({ x0, y, width, runStart, runEnd }) {
  const totalMs = runEnd - runStart;
  if (totalMs <= 0) return null;
  // Generate monthly ticks
  const ticks = [];
  const cur = new Date(runStart);
  cur.setDate(1);
  cur.setHours(0, 0, 0, 0);
  let safety = 0;
  while (cur <= runEnd && safety < 100) {
    safety++;
    if (cur >= runStart) {
      const x = x0 + ((cur - runStart) / totalMs) * width;
      const label = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,"0")}`;
      ticks.push({x, label});
    }
    cur.setMonth(cur.getMonth() + 1);
  }
  // Skip every Nth label if too many
  const labelSkip = ticks.length > 24 ? Math.ceil(ticks.length / 12) : 1;
  return (
    <g>
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={t.x} x2={t.x} y1={y} y2={y+4} stroke={theme.text4} strokeWidth={0.5}/>
          {(i % labelSkip === 0) && (
            <text x={t.x} y={y+15} fontSize={9} fill={theme.text3} fontFamily={fonts.mono} textAnchor="middle">
              {t.label}
            </text>
          )}
        </g>
      ))}
    </g>
  );
}

// ─── Statistik-Aggregation ──────────────────────────────────

/**
 * Berechne pro Daily-Regime-Bucket: Anzahl Trades, Sum PnL, PF, Anzahl Tage.
 */
function regimeStats(segments, trades) {
  // Gruppen-Keys: phase+trend kombination
  const buckets = {};
  for (const seg of segments) {
    const key = `${seg.phase}-${seg.trend}`;
    if (!buckets[key]) {
      buckets[key] = {
        phase: seg.phase, trend: seg.trend, label: seg.label, color: seg.color,
        days: 0, n_phases: 0, trades: 0, pnl: 0, wins: 0, losses: 0,
        gross_win: 0, gross_loss: 0,
      };
    }
    buckets[key].days += (seg.end - seg.start) / (1000*60*60*24);
    buckets[key].n_phases += 1;
  }
  // Trades zuordnen anhand mtf_daily_phase + mtf_daily_trend (direkt aus Trade-Feldern)
  for (const t of (trades || [])) {
    const key = `${t.mtf_daily_phase}-${t.mtf_daily_trend}`;
    if (!buckets[key]) continue;  // skip wenn Trade-State zu keinem Segment passt
    buckets[key].trades += 1;
    const pnl = t.pnl || 0;
    buckets[key].pnl += pnl;
    if (pnl > 0) { buckets[key].wins += 1; buckets[key].gross_win += pnl; }
    else         { buckets[key].losses += 1; buckets[key].gross_loss += Math.abs(pnl); }
  }
  // Sort by trades descending
  return Object.values(buckets).sort((a,b) => b.trades - a.trades);
}

// ─── Main Component ─────────────────────────────────────────

export function RegimeTab({ data }) {
  // v9.1b: mtf_regime_data ist single dict (einmal pro Run). Backward-Compat:
  // alte Runs hatten mtf_regime_data_per_trial — falls vorhanden, ersten Eintrag nehmen.
  const regimeData = useMemo(() => {
    if (data?.mtf_regime_data) return data.mtf_regime_data;
    const legacy = data?.mtf_regime_data_per_trial;
    if (legacy && typeof legacy === "object") {
      const first = Object.values(legacy).find(v => v && v.daily_transitions);
      if (first) return first;
    }
    return null;
  }, [data]);

  const tradesPerTrial = data?.trades_per_trial || {};

  // Available trials: alle Trials mit persistierten Trades (Regime ist global)
  const availableTrials = useMemo(() => {
    return Object.keys(tradesPerTrial)
      .map(k => parseInt(k, 10))
      .filter(n => !isNaN(n))
      .sort((a, b) => a - b);
  }, [tradesPerTrial]);

  // Default: erster verfügbarer Trial
  const [selectedTrial, setSelectedTrial] = useState(() => availableTrials[0] || null);
  const [hover, setHover] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);   // [Date, Date] for H1-detail

  // Default-Range: letzte 30 Tage des Run-Periods (wird in useEffect unten gesetzt
  // wenn runStart/runEnd verfügbar sind).

  if (!regimeData || availableTrials.length === 0) {
    return (
      <div style={{padding:32, textAlign:"center", color:theme.text3}}>
        <div style={{fontSize:32, marginBottom:8}}>🎯</div>
        <div style={{fontSize:14, fontWeight:600, marginBottom:6}}>Keine Regime-Daten verfügbar</div>
        <div style={{fontSize:12, lineHeight:1.5, maxWidth:500, margin:"0 auto"}}>
          Dieser Run wurde vor dem v9.1-Update gestartet. Regime-Visualisierung ist nur
          für Runs verfügbar die nach dem Backend-Update gelaufen sind. Starte einen neuen
          Run um die MTF-Regime-Analyse zu nutzen.
        </div>
      </div>
    );
  }

  const trialId = selectedTrial ?? availableTrials[0];
  const trades = tradesPerTrial[trialId] || tradesPerTrial[String(trialId)] || [];

  // Zeit-Range bestimmen aus Run-Periode + Trade-Daten
  const runStart = useMemo(() => {
    if (data?.start_date) {
      const d = new Date(data.start_date);
      if (!isNaN(d.getTime())) return d;
    }
    // Fallback: erster Daily-Transition
    const firstD = regimeData.daily_transitions?.[0];
    if (firstD) return parseDate(firstD.ts);
    return new Date();
  }, [data, regimeData]);

  const runEnd = useMemo(() => {
    if (data?.end_date) {
      const d = new Date(data.end_date);
      if (!isNaN(d.getTime())) return d;
    }
    return new Date();
  }, [data]);

  // Default H1-Range: letzte 30 Tage des Backtest-Periods. Wird einmalig gesetzt
  // wenn runEnd verfügbar ist und noch kein selectedRange.
  useEffect(() => {
    if (selectedRange || !runEnd) return;
    const defaultStart = new Date(runEnd.getTime() - 30 * 24 * 60 * 60 * 1000);
    const clampedStart = runStart && defaultStart < runStart ? runStart : defaultStart;
    setSelectedRange([clampedStart, runEnd]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runEnd?.getTime(), runStart?.getTime()]);

  const dailySegs = useMemo(() => buildSegments(regimeData.daily_transitions, runEnd), [regimeData, runEnd]);
  const h1Segs    = useMemo(() => buildSegments(regimeData.h1_transitions,    runEnd), [regimeData, runEnd]);

  const maxAbsPnl = useMemo(() => trades.reduce((m, t) => Math.max(m, Math.abs(t.pnl || 0)), 0), [trades]);

  const stats = useMemo(() => regimeStats(dailySegs, trades), [dailySegs, trades]);

  // SVG-Dimensions
  const W = 1100;
  const PAD_L = 60;
  const PAD_R = 20;
  const innerW = W - PAD_L - PAD_R;
  const stripH = 28;
  const stripGap = 8;
  const tradesH = 30;
  const axisH = 22;
  const H = stripH + stripGap + stripH + stripGap + tradesH + axisH + 20;

  const dailyY = 10;
  const h1Y    = dailyY + stripH + stripGap;
  const tradesY = h1Y + stripH + stripGap + tradesH/2;
  const axisY   = tradesY + tradesH/2 + 6;

  return (
    <div>
      {/* Trial Picker */}
      <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:14, padding:"10px 14px", background:theme.tint, borderRadius:8}}>
        <span style={{fontSize:11, fontWeight:700, color:theme.text3, textTransform:"uppercase"}}>Trial:</span>
        <select
          value={trialId}
          onChange={e => setSelectedTrial(parseInt(e.target.value, 10))}
          style={{padding:"5px 10px", fontSize:13, fontFamily:fonts.mono, fontWeight:700, border:`1px solid ${theme.border}`, borderRadius:6, background:theme.white, cursor:"pointer"}}
        >
          {availableTrials.map(id => <option key={id} value={id}>#{id}</option>)}
        </select>
        <span style={{fontSize:11, color:theme.text3, marginLeft:"auto"}}>
          {availableTrials.length} Trials mit Regime-Daten · {trades.length} Trades in #{trialId}
        </span>
      </div>

      {/* Legende */}
      <div style={{display:"flex", flexWrap:"wrap", gap:14, marginBottom:14, padding:"10px 14px", background:theme.tint, borderRadius:8, fontSize:11}}>
        <div style={{display:"flex", alignItems:"center", gap:5}}><span style={{width:14, height:10, background:"#16a34a", borderRadius:2}}/> ETAB Bull</div>
        <div style={{display:"flex", alignItems:"center", gap:5}}><span style={{width:14, height:10, background:"#dc2626", borderRadius:2}}/> ETAB Bear</div>
        <div style={{display:"flex", alignItems:"center", gap:5}}><span style={{width:14, height:10, background:"#f97316", borderRadius:2}}/> Pivot (Übergang)</div>
        <div style={{display:"flex", alignItems:"center", gap:5}}><span style={{width:14, height:10, background:"#cbd5e1", borderRadius:2}}/> Undefined</div>
        <div style={{height:14, width:1, background:theme.divider}}/>
        <div style={{display:"flex", alignItems:"center", gap:5}}><span style={{width:8, height:8, background:"#16a34a", borderRadius:"50%"}}/> Win</div>
        <div style={{display:"flex", alignItems:"center", gap:5}}><span style={{width:8, height:8, background:"#dc2626", borderRadius:"50%"}}/> Loss</div>
        <span style={{color:theme.text4, marginLeft:"auto"}}>Period: {formatDateShort(runStart)} → {formatDateShort(runEnd)}</span>
      </div>

      {/* Timeline Chart — v9.1c: Lightweight Charts + Split-View */}
      <div style={{marginBottom:14, position:"relative"}}>
        <RegimeChart
          dailyOhlc={regimeData.daily_ohlc || []}
          dailyTransitions={regimeData.daily_transitions || []}
          h1Transitions={regimeData.h1_transitions || []}
          trades={trades}
          runStart={runStart}
          runEnd={runEnd}
          onRangeSelect={(s, e) => setSelectedRange([s, e])}
          selectedRange={selectedRange}
          instrument={data?.instrument || "MGC"}
        />
      </div>

      {/* H1-Detail-Chart (Split-View unten) */}
      <div style={{marginBottom:14, position:"relative"}}>
        <H1DetailChart
          instrument={data?.instrument || "MGC"}
          selectedRange={selectedRange}
          h1Transitions={regimeData.h1_transitions || []}
          runEnd={runEnd}
          trades={trades}
        />
      </div>

      {/* Statistik-Tabelle */}
      <div style={{background:theme.white, padding:"14px 18px", borderRadius:8, border:`1px solid ${theme.divider}`}}>
        <div style={{fontSize:11, fontWeight:700, color:theme.text2, marginBottom:10, textTransform:"uppercase", letterSpacing:".5px"}}>Performance je Daily-Regime</div>
        <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
          <thead>
            <tr style={{borderBottom:`1px solid ${theme.divider}`}}>
              {["Regime", "Tage", "Phasen", "Trades", "Win-Rate", "PnL", "PF"].map(h =>
                <th key={h} style={{padding:"6px 8px", fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase", textAlign: h === "Regime" ? "left" : "right"}}>{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {stats.map((s, i) => {
              const wr = s.trades > 0 ? (s.wins / s.trades) * 100 : 0;
              const pf = s.gross_loss > 0 ? s.gross_win / s.gross_loss : (s.gross_win > 0 ? Infinity : 0);
              return (
                <tr key={i} style={{borderBottom:i < stats.length-1 ? `1px solid ${theme.divider}` : "none"}}>
                  <td style={{padding:"7px 8px"}}>
                    <div style={{display:"flex", alignItems:"center", gap:8}}>
                      <span style={{width:12, height:12, background:s.color, borderRadius:2, flexShrink:0}}/>
                      <span style={{fontWeight:600}}>{s.label}</span>
                    </div>
                  </td>
                  <td style={{padding:"7px 8px", textAlign:"right", fontFamily:fonts.mono}}>{Math.round(s.days)}</td>
                  <td style={{padding:"7px 8px", textAlign:"right", fontFamily:fonts.mono}}>{s.n_phases}</td>
                  <td style={{padding:"7px 8px", textAlign:"right", fontFamily:fonts.mono, fontWeight:700}}>{s.trades}</td>
                  <td style={{padding:"7px 8px", textAlign:"right", fontFamily:fonts.mono}}>{s.trades > 0 ? wr.toFixed(1)+"%" : "—"}</td>
                  <td style={{padding:"7px 8px", textAlign:"right", fontFamily:fonts.mono, fontWeight:700, color: s.pnl >= 0 ? theme.greenText : theme.redText}}>
                    {s.trades > 0 ? formatPnl(s.pnl) : "—"}
                  </td>
                  <td style={{padding:"7px 8px", textAlign:"right", fontFamily:fonts.mono}}>
                    {s.trades > 0 && pf !== Infinity ? pf.toFixed(2) : s.trades > 0 && pf === Infinity ? "∞" : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {stats.length === 0 && <div style={{padding:"20px", textAlign:"center", color:theme.text3, fontSize:12}}>Keine Regime-Statistik verfügbar.</div>}
      </div>
    </div>
  );
}
